import { Button, Chip } from '@sortlist-frontend/design-system';
import { useState } from 'react';

import type { GuideLabels, GuideTree } from '../types';

interface DecisionTreeProps {
  label: string;
  tree: GuideTree;
  labels: Pick<GuideLabels, 'yes' | 'no' | 'restart' | 'ourReading'>;
}

export const DecisionTree = (props: DecisionTreeProps) => {
  const { label, tree, labels } = props;
  const [current, setCurrent] = useState(tree.start);
  const [trail, setTrail] = useState<readonly string[]>([]);
  const question = tree.q[current];
  const result = tree.r[current];

  const answer = (next: string, answerLabel: string) => {
    if (question == null) return;
    setTrail([...trail, `${question.s} : ${answerLabel}`]);
    setCurrent(next);
  };

  const restart = () => {
    setTrail([]);
    setCurrent(tree.start);
  };

  return (
    <div className="rounded-2xl border border-primary-200 bg-primary-50 p-5 md:p-8">
      <ol aria-label={label} className="mb-4 flex min-h-8 flex-wrap gap-2">
        {trail.map((step) => (
          <li key={step}>
            <Chip color="primary" variant="flat" radius="full" size="sm">
              {step}
            </Chip>
          </li>
        ))}
      </ol>
      {question != null ? (
        <div aria-live="polite">
          <p className="text-xl font-bold leading-snug text-secondary-950">{question.t}</p>
          {question.h != null ? <p className="mt-2 text-sm text-secondary-500">{question.h}</p> : null}
          <div className="mt-5 flex flex-wrap gap-3">
            <Button color="primary" variant="solid" size="md" onPress={() => answer(question.y, labels.yes)}>
              {labels.yes}
            </Button>
            <Button color="primary" variant="bordered" size="md" onPress={() => answer(question.n, labels.no)}>
              {labels.no}
            </Button>
          </div>
        </div>
      ) : result != null ? (
        <div aria-live="polite">
          <p className="text-xs font-bold uppercase tracking-wide text-secondary-500">{labels.ourReading}</p>
          <p className="mt-1 text-2xl font-extrabold text-secondary-950">{result.t}</p>
          <p className="mt-3 leading-relaxed text-secondary-700">{result.d}</p>
          <div className="mt-5">
            <Button color="primary" variant="bordered" size="md" onPress={restart}>
              {labels.restart}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
